import ReactGA from 'react-ga4';

let isInitialized = false;

/**
 * Controlla se l'utente ha dato il consenso per gli analytics
 */
export const hasAnalyticsConsent = (): boolean => {
  try {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.consent === 'accepted';
  } catch {
    return false;
  }
};

/**
 * Inizializza Google Analytics (solo se consent dato)
 */
export const initializeAnalytics = () => {
  if (isInitialized) return;

  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (measurementId && hasAnalyticsConsent()) {
    ReactGA.initialize(measurementId);
    isInitialized = true;
  }
};

/**
 * Inizializza GA4 senza check consent (per quando banner è disabilitato)
 */
export const initializeAnalyticsUnconditional = () => {
  if (isInitialized) return;

  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (measurementId) {
    ReactGA.initialize(measurementId);
    isInitialized = true;
  }
};

/**
 * Traccia una visualizzazione di pagina
 */
export const trackPageView = (path: string, title?: string) => {
  if (!isInitialized) return;
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: title || document.title
  });
};

/**
 * Traccia un evento personalizzato
 */
export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  if (!isInitialized) return;
  ReactGA.event({ category, action, label, value });
};

/**
 * Traccia una conversione
 */
export const trackConversion = (conversionName: string, value?: number) => {
  trackEvent('conversion', conversionName, undefined, value);
};

/**
 * Traccia il tempo di permanenza su una pagina
 */
export const trackTimeOnPage = (pageName: string, timeInSeconds: number) => {
  trackEvent('engagement', 'time_on_page', pageName, timeInSeconds);
};

/**
 * Traccia i click su link esterni
 */
export const trackExternalLink = (url: string) => {
  trackEvent('engagement', 'external_link_click', url);
};

/**
 * Traccia i download di file
 */
export const trackFileDownload = (fileName: string, fileType: string) => {
  trackEvent('engagement', 'file_download', `${fileName} (${fileType})`);
};

/**
 * Traccia gli errori
 */
export const trackError = (errorMessage: string, errorSource?: string) => {
  trackEvent('error', 'exception', `${errorSource || 'unknown'}: ${errorMessage}`);
};

/**
 * Traccia le ricerche
 */
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent('engagement', 'search', searchTerm, resultsCount);
};

/**
 * Traccia i login
 */
export const trackLogin = (userType: string) => {
  trackEvent('user', 'login', userType);
};

/**
 * Traccia i logout
 */
export const trackLogout = () => {
  trackEvent('user', 'logout');
};

/**
 * Traccia le registrazioni
 */
export const trackSignup = (userType: string) => {
  trackEvent('user', 'signup', userType);
};

/**
 * Traccia i pagamenti
 */
export const trackPayment = (paymentType: string, amount: number, currency: string = 'EUR') => {
  trackEvent('ecommerce', 'purchase', `${paymentType} - ${amount} ${currency}`, amount);
};

const analyticsService = {
  hasAnalyticsConsent,
  initializeAnalytics,
  initializeAnalyticsUnconditional,
  trackPageView,
  trackEvent,
  trackConversion,
  trackTimeOnPage,
  trackExternalLink,
  trackFileDownload,
  trackError,
  trackSearch,
  trackLogin,
  trackLogout,
  trackSignup,
  trackPayment
};

export default analyticsService;
